import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../../../utils/createNotification';
import './PendingChangeControl.scss';

export interface PendingChangeControlProps {
    canApprove: boolean;
    onApprove: () => Promise<void>;
    onReject: () => Promise<void>;
    onDiscard?: () => Promise<void>;
    children: React.ReactNode;
}

export const PendingChangeControl = (
    props: PendingChangeControlProps
) => {
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);

    const handleApprove = async () => {
        setIsApproving(true);
        try {
            await props.onApprove();
            createNotification('Proposed change approved', 'success');
        } catch {
            createNotification('Failed to approve the proposed change', 'error');
        } finally {
            setIsApproving(false);
        }
    };

    const handleReject = async () => {
        setIsRejecting(true);
        try {
            await props.onReject();
            createNotification('Proposed change rejected', 'success');
        } catch {
            createNotification('Failed to reject the proposed change', 'error');
        } finally {
            setIsRejecting(false);
        }
    };

    const handleDiscard = async () => {
        setIsDiscarding(true);
        try {
            await props.onDiscard?.();
            createNotification('Proposed change discarded', 'success');
        } catch {
            createNotification('Failed to discard the proposed change', 'error');
        } finally {
            setIsDiscarding(false);
        }
    };

    return (
        <div className="pending-change-control">
            <div className="pending-change-control-value">{props.children}</div>
            {props.canApprove ? (
                <div className="pending-change-control-actions">
                    <FontAwesomeIcon
                        icon={faCheck}
                        className="pending-change-control-icon pending-change-control-approve-icon"
                        onClick={() => setShowApproveModal(true)}
                        title="Approve proposed change"
                    />
                    <FontAwesomeIcon
                        icon={faTimes}
                        className="pending-change-control-icon pending-change-control-reject-icon"
                        onClick={() => setShowRejectModal(true)}
                        title="Reject proposed change"
                    />
                    {ReactDOM.createPortal(
                        <>
                            <ConfirmationModal
                                isVisible={showApproveModal}
                                setIsVisible={setShowApproveModal}
                                header="Approve Proposed Change"
                                confirmationText="Are you sure you want to approve the proposed change?"
                                nextButtonText="Approve"
                                cancelButtonText="Cancel"
                                onConfirm={handleApprove}
                                closeOnConfirm={true}
                                isLoading={isApproving}
                            />
                            <ConfirmationModal
                                isVisible={showRejectModal}
                                setIsVisible={setShowRejectModal}
                                header="Reject Proposed Change"
                                confirmationText="Are you sure you want to reject the proposed change?"
                                nextButtonText="Reject"
                                cancelButtonText="Cancel"
                                isConfirmationDanger={false}
                                onConfirm={handleReject}
                                closeOnConfirm={true}
                                isLoading={isRejecting}
                            />
                        </>,
                        document.body
                    )}
                </div>
            ) : (
                <div className="pending-change-control-actions">
                    <span className="pending-change-control-badge">Pending buyer approval</span>
                    {props.onDiscard && (
                        <>
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="pending-change-control-icon pending-change-control-discard-icon"
                                onClick={() => setShowDiscardModal(true)}
                                title="Discard proposed change"
                            />
                            {ReactDOM.createPortal(
                                <ConfirmationModal
                                    isVisible={showDiscardModal}
                                    setIsVisible={setShowDiscardModal}
                                    header="Discard Proposed Change"
                                    confirmationText="Are you sure you want to discard this proposed change?"
                                    nextButtonText="Discard"
                                    cancelButtonText="Cancel"
                                    isConfirmationDanger={false}
                                    onConfirm={handleDiscard}
                                    closeOnConfirm={true}
                                    isLoading={isDiscarding}
                                />,
                                document.body
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
